import { createContext, useState, useEffect, ReactNode } from 'react';
import { auth, db } from '@/services/firebase/config';
import { onAuthStateChanged, User, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { clearAllVoiceQueues } from '@/services/voice/queueStore';
import { clearVoiceRecordMemory } from '@/services/voice/voiceRecord';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ClinicService } from '@/services/firebase/clinic';
import { UserProfile, Role, Permissions } from '@/types/auth';
import { clearAllAudios, purgeStaleAudios } from '@/services/session/audioSessionStore';
import { clearAllSessionDrafts, purgeStaleSessionDrafts } from '@/services/session/sessionDraftStore';
import { clearAllJobs } from '@/services/session/extractionManager';
import { parseUnitAccess, type UnitAccessGrant } from '@/utils/unitAccess';
import { iniciarPrefsNaConta, pararPrefsNaConta } from '@/services/prefs/prefsNaConta';

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  sessionTimeoutMinutes: number;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

// Default: 15 min. 0 = desabilitado (nunca desloga por inatividade).
const DEFAULT_SESSION_TIMEOUT_MINUTES = 15;

export const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  sessionTimeoutMinutes: DEFAULT_SESSION_TIMEOUT_MINUTES,
  loginWithGoogle: async () => {},
  logout: async () => {}
});

async function ensureClinicAndProfessional(firebaseUser: User, clinicId: string, role: Role) {
  try {
    // Clínica nova só nasce pelo backend (firestore.rules nega create desde
    // 18/10/2026) — sem a clínica, não há doc de equipe nem unidade a criar.
    const clinicRef = doc(db, 'clinics', clinicId);
    const clinicSnap = await getDoc(clinicRef);
    if (!clinicSnap.exists()) return;

    // Ensure SYSADMIN clinic has unlimited tokens
    const clinicData = clinicSnap.data();
    if (clinicData?.ownerId === firebaseUser.uid && !clinicData?.unlimitedTokens) {
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(clinicRef, { unlimitedTokens: true, aiTokenBalance: 999999 });
      console.log('✅ Clinic upgraded to unlimited tokens');
    }

    // Only create professional doc if it doesn't exist
    const profRef = doc(db, 'clinics', clinicId, 'professionals', firebaseUser.uid);
    const profSnap = await getDoc(profRef);
    if (!profSnap.exists()) {
      await setDoc(profRef, {
        id: firebaseUser.uid,
        clinicId,
        unitIds: [],
        name: firebaseUser.displayName || 'Usuário',
        email: firebaseUser.email || '',
        role,
        permissions: {},
        hasCompletedTour: false,
        isActive: true,
        createdAt: Date.now(),
      });
      console.log('✅ Professional document created');
    }

    // Garante uma unidade padrão para clínicas sem nenhuma cadastrada.
    // O portal do paciente, o agendamento e o agendador externo dependem
    // disso para funcionar — todo appointment é criado com unitId='unit-1'.
    // Operação idempotente: roda só se a coleção estiver vazia.
    const unitRef = doc(db, 'clinics', clinicId, 'units', 'unit-1');
    const unitSnap = await getDoc(unitRef);
    if (!unitSnap.exists()) {
      await setDoc(unitRef, {
        id: 'unit-1',
        clinicId,
        name: 'Sede',
        isActive: true,
        createdAt: Date.now(),
      });
      console.log('✅ Default unit created (unit-1)');
    }
  } catch (err) {
    console.error('Error provisioning clinic/professional:', err);
    // Re-throw so the auth flow can show an error if needed
    console.warn('⚠️ Clinic/Professional provisioning failed. User may not have write permissions.');
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState<number>(DEFAULT_SESSION_TIMEOUT_MINUTES);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        try {
          const idTokenResult = await firebaseUser.getIdTokenResult();
          const claims = idTokenResult.claims;

          // Pacientes logados no /portal compartilham o mesmo `auth.currentUser`
          // que o staff. Sem esse guard, o AuthContext criaria um doc em
          // clinics/{id}/professionals pro paciente (ensureClinicAndProfessional)
          // e o treat-em-as-staff entraria em colisão com as rotas protegidas.
          // Para role=PACIENTE, mantemos `user` populado (necessário pro PortalAuthContext
          // enxergar o login) mas zeramos `profile` — `isAuthenticated` nos guards
          // do staff checa `user && profile`, então /pacientes, /agenda etc não
          // deixam um paciente entrar.
          if (claims.role === 'PACIENTE') {
            void pararPrefsNaConta();
            setProfile(null);
            setSessionTimeoutMinutes(DEFAULT_SESSION_TIMEOUT_MINUTES);
            setLoading(false);
            return;
          }

          const clinicId = (claims.clinicId as string) || 'default-clinic';
          const role = (claims.role as Role) || Role.ADMIN;

          // Configurações que seguem a pessoa em qualquer computador: lê a
          // conta junto com o resto e só libera as telas depois (teto de
          // 2,5 s), pra elas já montarem com as preferências dela.
          const prefs = iniciarPrefsNaConta(firebaseUser.uid);

          // Quando o usuário está aceitando um convite, NÃO provisiona doc em
          // default-clinic — caso contrário ele acabaria como ADMIN da
          // default-clinic além do convite. A AcceptInvitePage vai chamar
          // /accept-invite que cria o doc no clinicId correto. Detecta o
          // contexto de convite olhando o pathname + token na URL.
          const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
          const isAcceptingInvite =
            !!url &&
            url.pathname.startsWith('/accept-invite') &&
            !!url.searchParams.get('token');

          if (!isAcceptingInvite) {
            // Auto-provision clinic and professional documents if they don't exist
            await ensureClinicAndProfessional(firebaseUser, clinicId, role);
          }

          // Failsafe: limpa áudios de sessão que sobraram de dias anteriores
          // ou de outro usuário (caso o usuário tenha fechado a aba sem
          // clicar em Sair). Os áudios do dia atual do próprio usuário
          // permanecem para serem reidratados na ficha do paciente.
          purgeStaleAudios(firebaseUser.uid).catch(() => {});
          purgeStaleSessionDrafts(firebaseUser.uid).catch(() => {});

          // Carrega o tempo de sessao configurado na clinica (default 15 min).
          try {
            const clinicDoc = await ClinicService.getClinicDoc(clinicId);
            const raw = clinicDoc?.sessionTimeoutMinutes;
            if (typeof raw === 'number' && raw >= 0) {
              setSessionTimeoutMinutes(raw);
            } else {
              setSessionTimeoutMinutes(DEFAULT_SESSION_TIMEOUT_MINUTES);
            }
          } catch {
            setSessionTimeoutMinutes(DEFAULT_SESSION_TIMEOUT_MINUTES);
          }

          // Escopo de agenda (quais médicos este perfil pode mexer). Lido
          // do doc do profissional, não das claims: claim só atualiza no
          // próximo login, e tirar o acesso de alguém não pode esperar
          // ele deslogar. Falha na leitura = escopo vazio = sem restrição
          // na tela; a trava de escrita continua valendo nas rules.
          // Mesma leitura traz o acesso às unidades (`unitAccess`, spec 019)
          // e onde a pessoa atende (`unitIds` do doc — as claims podem estar
          // velhas).
          let allowedProfessionalIds: string[] = [];
          let serviceLocal: string | undefined;
          let unitAccess: UnitAccessGrant;
          let docUnitIds: string[] | undefined;
          try {
            const meSnap = await getDoc(doc(db, 'clinics', clinicId, 'professionals', firebaseUser.uid));
            const me = meSnap.exists()
              ? (meSnap.data() as { allowedProfessionalIds?: unknown; serviceLocal?: unknown; unitAccess?: unknown; unitIds?: unknown })
              : null;
            const raw = me?.allowedProfessionalIds;
            if (Array.isArray(raw)) {
              allowedProfessionalIds = raw.filter((v): v is string => typeof v === 'string' && !!v);
            }
            if (typeof me?.serviceLocal === 'string' && me.serviceLocal) serviceLocal = me.serviceLocal;
            unitAccess = parseUnitAccess(me?.unitAccess);
            if (Array.isArray(me?.unitIds)) {
              docUnitIds = me.unitIds.filter((v): v is string => typeof v === 'string' && !!v);
            }
          } catch (err) {
            console.error('[auth] falha ao ler escopo de agenda:', err);
          }

          await prefs;

          setProfile({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            role,
            clinicId,
            permissions: (claims.permissions as Permissions) || {},
            unitIds: docUnitIds ?? ((claims.unitIds as string[]) || []),
            allowedProfessionalIds,
            serviceLocal,
            unitAccess,
          });
        } catch (error) {
          console.error("Error fetching rules:", error);
          setProfile(null);
        }
      } else {
        void pararPrefsNaConta();
        setProfile(null);
        setSessionTimeoutMinutes(DEFAULT_SESSION_TIMEOUT_MINUTES);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    // Força o seletor de contas do Google a aparecer toda vez, mesmo quando há
    // uma única sessão ativa no navegador. Sem isso o Google reautentica direto
    // na conta logada e o usuário não consegue escolher outra conta.
    provider.setCustomParameters({ prompt: 'select_account' });
    await signInWithPopup(auth, provider);
  };

  const logout = async () => {
    // Descarta TODOS os áudios de sessão do dia antes do signOut.
    // Requisito: ao clicar em Sair, os áudios dos exames realizados
    // durante o dia são apagados do navegador.
    try {
      clearAllJobs();
      // Documentos ditados e ainda não impressos morrem com a sessão —
      // nenhuma fila de paciente atravessa a troca de usuário (spec 009).
      clearAllVoiceQueues();
      clearVoiceRecordMemory();
      await Promise.all([clearAllAudios(), clearAllSessionDrafts()]);
    } catch (err) {
      console.warn('Falha ao limpar sessões no logout:', err);
    }
    // Ainda logado: o que mudou nas configurações agora há pouco sobe pra conta.
    await pararPrefsNaConta();
    await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, sessionTimeoutMinutes, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
